import React, {Fragment, useMemo} from 'react';
import {Icon, Flex, Breadcrumbs, Link, Layout, Form, Edit, Textarea, Date, TimePicker, Tags, List, Diary, Redirect, Button} from 'components';
import {over, toPairs, keys, moment, pick, debounce, useNamedSelector, useWindowSize, useFormikContext} from 'helpers';
import {store} from 'store';
import {forbid} from 'store/session';

export function EditForm({id, modal, context = {}, ...props}) {
  const permissions = useNamedSelector('permissions.list');
  const { today } = useNamedSelector('calendar.days');
  const { date = today.unix() } = props;
  const { diary, special, mood, questions, saved } = useNamedSelector('diary.details', { id: id, context: 'journals' });
  const { height } = useWindowSize();

  const initialValues = useMemo(() => ({
    date,
    questions,
    subject: context.goal?.name || context.activity?.name || '',
    tagIds: '',
    text: '',
    time: moment().format('h:mm A'),
    goalId: context.goal?.id,
    taskId: context.activity?.id,
    ...diary,
  }), [ id, diary]);

  return (
    <Fragment>
      { id && !diary && !modal && <Redirect to="/diary/" /> }

      <Layout.Header justified>
        <Breadcrumbs>
          { context.goal && <Link to="/goals/">Goals</Link> }
          { context.goal && <Link to={`/goals/${context.goal.id}/`}>{context.goal.name}</Link> }
          <Link to="/diary/">Journal</Link>
          { !context.goal && moment.unix(date).calendar() }
          <span>
            <b>{ id ? 'Edit' : 'Add' } </b>
            { !saved && <Icon className="secondary" name="spinner" /> }
          </span>
        </Breadcrumbs>
        { !modal &&
          <Button className={['secondary', 'small', !id && 'invisible']} icon="plus" to={permissions.canAdd.diary ? '/diary/add/' : undefined} onClick={permissions.canAdd.diary ? undefined : forbid}>
            Add Record
          </Button>
        }
      </Layout.Header>

      <div className="spacing">
        { mood
          ? <Diary.MoodMeter {...mood} date={diary.date} />
          : <Form id="journal" initialValues={initialValues} onChange={!modal && save} onSubmit={modal && over(save, modal.back)} delay>
            <List small-gutter>
              { special
                ? <h1>{ diary.subject }</h1>
                : <Fragment>
                  { !context.goal &&
                    <Edit name="subject" placeholder="Subject" bold autoFocus={!id} />
                  }
                  <Textarea name="text" placeholder="Enter text here.." required={Boolean(modal)} minRows={Math.round((height - 450)/32)} />
                </Fragment>
              }
              { keys(questions).map(question =>
                <div key={question}>
                  <b className="label">{question}</b>
                  <Textarea name={'questions.' + question} single />
                </div>
              ) }
              <Date name="date" label="Date" required />
              <TimePicker name="time" label="Time" required />
              <Tags name="tagIds" />
            </List>
          </Form>
        }
      </div>

      { modal &&
        <Flex className={['right', 'spacing', 'small']}>
          <Button onClick={modal.back} className="borderless">Cancel</Button>
          <Button form="journal" className="primary" type="submit">Save</Button>
        </Flex>
      }
    </Fragment>
  )
}

const save = debounce((diary) => {
  if (diary.id) {
    if (diary.questions) {
      diary.text = toPairs(diary.questions)
        .map(([ question, answer ]) => question.concat('\n', answer || '-'))
        .join('\n\n');
    }
    store.dispatch({
      type: 'DIARY_UPDATE',
      diary: pick(diary, 'id', 'text', 'subject', 'date', 'tagIds', 'time', 'goalId', 'taskId'),
    });
  } else if (diary.subject || diary.text) {
    store.dispatch({
      type: 'DIARY_UPDATE',
      diary: pick(diary, 'id', 'text', 'subject', 'date', 'tagIds', 'time', 'goalId', 'taskId'),
      historyReplace: !diary.goalId && !diary.taskId && `/diary/${diary.date}/{id}/`,
    });
  }
}, 200);
